import React, { useEffect, useRef } from "react";
import * as d3 from "d3";
import {
  convertUTCtoLocalDate,
  convertUTCtoLocalTimeWithAMorPM,
} from "../../../utils/CommonUtils";
import loaderGif from '../../../assets/Images/loader.gif';

const RomLineChart = ({ data, yAxisTickFormat }) => {
  const ref = useRef();

  useEffect(() => {
    console.log("Data:", data);
    d3.select(ref.current).selectAll("*").remove();

    const margin = 50;
    const width = 750;
    const height = 212;
    const duration = 250;
    const lineOpacity = "0.25";
    const lineOpacityHover = "0.85";
    const otherLinesOpacityHover = "0.1";
    const lineStroke = "1.5px";
    const lineStrokeHover = "2.5px";
    const circleOpacity = "0.85";
    const circleOpacityOnLineHover = "0.25";
    const circleRadius = 3;
    const circleRadiusHover = 6;

    const parseDate = d3.timeParse("%Y-%m-%dT%H:%M:%S.%fZ");
    const year = 2023;
    const parsedData = data.map((item) => ({
      ...item,
      values: item.values.map((d) => ({
        x: parseDate(d.x),
        y: +d.y,
        name: item.name,
        yAxisTickFormat: yAxisTickFormat,
        localTimeAndDate: d.x,
        peak_frame_url: d.peak_frame_url,
      })),
    }));
    const xScale = d3
      .scaleUtc()
      .domain([new Date(year, 0, 1), new Date(year, 11, 31)]) // From Jan 1 to Dec 31
      .range([0, width - margin]);

    const yScale = d3
      .scaleLinear()
      .domain([
        0,
        d3.max(
          parsedData.flatMap((d) => d.values),
          (v) => v.y
        ),
      ])
      .range([height - margin, 0]);

    // const color = d3.scaleOrdinal(d3.schemeCategory10);
    const customTicks = Array.from(
      { length: 12 },
      (_, i) => new Date(year, i, 1) // This will give the first day of each month
    );
    const dateFormatter = d3.timeFormat("%b"); // This will display only the month's abbreviation

    const xAxis = d3
      .axisBottom(xScale)
      .tickValues(customTicks)
      .tickFormat(dateFormatter);

    /* Add Axis into SVG */
    // var xAxis = d3.axisBottom(xScale).ticks(5);
    // var yAxis = d3.axisLeft(yScale).ticks(5);
    var yAxis = d3
      .axisLeft(yScale)
      .ticks(5)
      .tickFormat((d) => `${d} ${yAxisTickFormat}`);

    const svg = d3
      .select(ref.current)
      .append("svg")
      .attr("width", "100%")
      .attr("height", "100%")
      .attr("viewBox", `0 0 ${width + margin} ${height + margin}`)
      .append("g")
      .attr("transform", `translate(${margin}, ${margin})`);
    svg
      .selectAll("xGrid")
      .data(customTicks)
      .enter()
      .append("line")
      .attr("class", "xGrid")
      .attr("x1", (d) => xScale(d))
      .attr("x2", (d) => xScale(d))
      .attr("y1", 0)
      .attr("y2", height - margin)
      .style("stroke", "#d4d4d4")
      .style("stroke-dasharray", "2,2");

    svg
      .append("g")
      .attr("class", "x axis")
      .attr("transform", `translate(0, ${height - margin})`)
      .call(xAxis)
      .attr("stroke", "none"); // set the stroke to none or transparent

    svg.selectAll(".tick line").remove();
    svg
      .append("g")
      .attr("class", "y axis")
      .call(yAxis)
      .append("text")
      .attr("y", 15)
      .attr("transform", "rotate(-90)")
      .attr("fill", "#000");
    svg
      .select(".x.axis")
      .selectAll("text")
      .attr("transform", "rotate(-45)")
      .attr("text-anchor", "end")
      .attr("dy", ".15em")
      .attr("dx", "-.8em");
    svg.selectAll(".tick line").remove();
    svg.select(".x.axis .domain").remove();
    svg.select(".y.axis .domain").remove();
    const tooltipGroup = svg.append("g").attr("class", "tooltip-group");
    const line = d3
      .line()
      .x((d) => {
        const scaledX = xScale(d.x);
        console.log("Scaled X:", scaledX, "for date", d.x);
        return scaledX;
      })
      .y((d) => {
        const scaledY = yScale(d.y);
        console.log("Scaled Y:", scaledY, "for value", d.y);
        return scaledY;
      });

    let lines = svg.append("g").attr("class", "lines");
    lines
      .selectAll(".line-group")
      .data(parsedData)
      .enter()
      .append("g")
      .attr("class", "line-group")
      .on("mouseover", function (d, i) {
        console.log("Mouseover:", d, i);
        svg
          .append("text")
          .attr("class", "title-text")
          .style("fill", d.color)
          .text(d.name)
          .attr("text-anchor", "middle")
          .attr("x", (width - margin) / 2)
          .attr("y", 5);
      })
      .on("mouseout", function (d) {
        svg.select(".title-text").remove();
      })
      .append("path")
      .attr("class", "line")
      .attr("d", (d) => line(d.values))
      .style("stroke", (d) => d.color)
      .style("fill", "none") // ADD THIS LINE HERE
      .style("opacity", lineOpacity)
      .on("mouseover", function (d) {
        d3.selectAll(".line").style("opacity", otherLinesOpacityHover);
        d3.selectAll(".circle").style("opacity", circleOpacityOnLineHover);
        d3.select(this)
          .style("opacity", lineOpacityHover)
          .style("stroke-width", lineStrokeHover)
          .style("cursor", "pointer");
      })
      .on("mouseout", function (d) {
        d3.selectAll(".line").style("opacity", lineOpacity);
        d3.selectAll(".circle").style("opacity", circleOpacity);
        d3.select(this)
          .style("stroke-width", lineStroke)
          .style("cursor", "none");
      });

    /* Add circles in the line */
    lines
      .selectAll("circle-group")
      .data(parsedData)
      .enter()
      .append("g")
      .style("fill", (d) => d.color)
      .selectAll("circle")
      .data((d) => d.values)
      .enter()
      .append("g")
      .attr("class", "circle")
      .on("mouseover", function (event, d) {
        const [x, y] = d3.pointer(event);

        // Fetch all overlapping data points
        const overlappingData = parsedData.flatMap((dataset) =>
          dataset.values
            .filter((val) => val.x.getTime() === d.x.getTime() && val.y === d.y)
            .map((val) => ({
              ...val,
              name: dataset.name,
              color: dataset.color,
            }))
        );

        const tooltipGroup = d3
          .select(this.parentNode)
          .append("g")
          .attr("class", "tooltip-group")
          .attr("transform", `translate(${xScale(d.x)}, ${yScale(d.y)})`);

        let yOffset = 0;
        overlappingData.forEach((overlap) => {
          // First line: Name
          tooltipGroup
            .append("text")
            .attr("class", "tooltip-text")
            .attr("x", 10)
            .attr("y", 20 + yOffset)
            .text(overlap.name)
            .style("fill", overlap.color)
            .style("font-size", "10px");

          yOffset += 20; // Adjust for next line

          // Second line: y on x
          const date = new Date(overlap.x);
          // Here's the updated code to format the date and time in a user-friendly way
          const formattedDate = `${convertUTCtoLocalDate(
            overlap.localTimeAndDate
          )} ${convertUTCtoLocalTimeWithAMorPM(overlap.localTimeAndDate)}`;
          const textContent = `${overlap.y}${
            overlap.yAxisTickFormat ? overlap.yAxisTickFormat : ""
          } on ${formattedDate}`;

          tooltipGroup
            .append("text")
            .attr("class", "tooltip-text")
            .attr("x", 10)
            .attr("y", 20 + yOffset)
            .text(textContent)
            .style("font-size", "10px")
            .style("fill", overlap.color);

          yOffset += 30; // Adjust for the next data point
          let imgWidth = 150;
          let imgHeight = 100;

          const maxImgWidth = width - x - 20; // 20 is some margin
          const maxImgHeight = height - y - 20;

          if (imgWidth > maxImgWidth) {
            imgWidth = maxImgWidth;
          }
          if (imgHeight > maxImgHeight) {
            imgHeight = maxImgHeight;
          }
          if (overlap.peak_frame_url) {
            // Append the loader
            tooltipGroup
              .append("foreignObject")
              .attr("x", 10)
              .attr("y", yOffset)
              .attr("width", imgWidth) // use the adjusted width
              .attr("height", imgHeight) // use the adjusted height
              .append("xhtml:div")
              .attr("class", "image-loader")
              .style("width", "100%")
              .style("height", "100%")
              .style("display", "flex")
              .style("align-items", "center")
              .style("justify-content", "center")
              .style("background-color", "#373D4B")
              .style("border-radius", "10px")
              .style("filter", "0px 0px 4px 0px rgba(43, 43, 44, 0.10)")
              .append("xhtml:img")
              .attr("src", loaderGif)
              .style("width", "50px")
              .style("height", "50px");


            // Append the image
            tooltipGroup
              .append("image")
              .attr("href", overlap.peak_frame_url) // replace with your image URL
              .attr("x", 10)
              .attr("y", yOffset)
              .attr("width", imgWidth) // use the adjusted width
              .attr("height", imgHeight) // use the adjusted height
              .on("load", function () {
                // Once the image is loaded, hide the loader
                d3.select(this.parentNode).select(".image-loader").remove();
              });
          }
        });
        const bbox = tooltipGroup.node().getBBox();
        let tooltipX = xScale(d.x);
        let tooltipY = yScale(d.y);

        if (x + bbox.width > width) {
          tooltipX -= bbox.width;
        }

        if (y + bbox.height > height) {
          tooltipY -= bbox.height;
        }

        tooltipGroup.attr("transform", `translate(${tooltipX}, ${tooltipY})`);
        tooltipGroup
          .insert("rect", "text")
          .attr("x", bbox.x - 5)
          .attr("y", bbox.y - 5)
          .attr("width", bbox.width + 10)
          .attr("height", bbox.height + 10)
          .style("fill", "#373D4B")
          .attr("rx", 10)
          .style("filter", "0px 0px 4px 0px rgba(43, 43, 44, 0.10)");
        // text size
        tooltipGroup.selectAll("text").attr("font-size", "12px");
        this.parentNode.parentNode.appendChild(this.parentNode);
      })

      .on("mouseout", function (d) {
        d3.select(this.parentNode).select(".tooltip-group").remove();
      })

      .append("circle")
      .attr("cx", (d) => xScale(d.x))
      .attr("cy", (d) => yScale(d.y))
      .attr("r", circleRadius)
      .style("opacity", circleOpacity)
      .on("mouseover", function (d) {
        d3.select(this)
          .transition()
          .duration(duration)
          .attr("r", circleRadiusHover);
        this.parentNode.appendChild(this);
      })
      .on("mouseout", function (d) {
        d3.select(this).transition().duration(duration).attr("r", circleRadius);
      });
    // Extracting x-values from the data
    // Extracting unique x-values from the data
    const tickValues = [
      ...new Set(parsedData.flatMap((d) => d.values.map((v) => v.x))),
    ];

    console.log("Tick values:", tickValues);
    // Using those values for the x-axis ticks

    // const dateFormatter = d3.timeFormat("%b %d %Y");

    svg.selectAll(".tick line").remove();
    svg
      .append("g")
      .attr("class", "y axis")
      .call(yAxis)
      .append("text")
      .attr("y", 15)
      .attr("transform", "rotate(-90)")
      .attr("fill", "#000");
    svg
      .select(".x.axis")
      .selectAll("text")
      .attr("transform", "rotate(-45)")
      .attr("text-anchor", "end")
      .attr("dy", ".15em")
      .attr("dx", "-.8em");
    svg.selectAll(".tick line").remove();
    svg.select(".x.axis .domain").remove();
    svg.select(".y.axis .domain").remove();

    svg.selectAll(".tick line").remove();
    svg
      .append("g")
      .attr("class", "y axis")
      .call(yAxis)
      .append("text")
      .attr("y", 15)
      .attr("transform", "rotate(-90)")
      .attr("fill", "#000");
    svg
      .select(".x.axis")
      .selectAll("text")
      .attr("transform", "rotate(-45)")
      .attr("text-anchor", "end")
      .attr("dy", ".15em")
      .attr("dx", "-.8em");
    svg.selectAll(".tick line").remove();
    svg.select(".x.axis .domain").remove();
    svg.select(".y.axis .domain").remove();
  }, [data]);

  return <div ref={ref}></div>;
};

export default RomLineChart;
